// Mutations for the files-management page: open one upload (presigned
// GET URL), delete one or more uploads, and refresh one or more
// Drive-sourced uploads. Each mutation invalidates the
// useUploadsList query for the project so the page list re-renders.
import { useMutation, useQueryClient } from "react-query";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { refetchDriveFileById, runWithConcurrency } from "../drive";
import { ensureGsiLoaded, requestGoogleAccessToken } from "../google-auth";
import { GOOGLE_DRIVE_SCOPE, GOOGLE_GMAIL_SCOPE } from "../google-config";
import {
  assembleThreadManifest,
  pullAttachments,
} from "./useGmailThreadImport";
import { fetchThreadStructured, getThreadProfileEmail } from "../gmail";
import { useAgentsHttp } from "../useAgentsHttp";
import { queryKeys } from "../queryKeys";
import { invalidateOperationsDashboard } from "./useOperationsDashboard";
import type { UploadRecord } from "./useUploadsList";

interface DownloadUrlResponse {
  url: string;
  original_name: string;
  content_type: string;
}

interface DeleteResponse {
  deleted: string[];
  missing: string[];
}

interface RefreshInitiateResponse {
  upload_id: string;
  source: "local" | "drive" | "gmail" | string;
  drive_file_id?: string | null;
  gmail_thread_id?: string | null;
  presigned_url: string;
  s3_key: string;
  expires_at: string;
  max_bytes: number;
}

export interface RefreshResult {
  refreshed: string[];
  errors: { upload_id: string; message: string }[];
}

// Hook: request a short-lived presigned GET URL for one upload.
// Returns the URL so the caller can open it in a new tab. The hook
// doesn't navigate itself; the caller decides (e.g. window.open).
export const useUploadDownloadUrl = () => {
  const http = useAgentsHttp();
  return useMutation<DownloadUrlResponse, Error, string>(
    async (upload_id) =>
      http<DownloadUrlResponse>("/uploads/download_url", { upload_id }),
  );
};

// Hook: delete one or more uploads. Pass the project_id at hook
// construction time so the cache invalidation can target the right
// useUploadsList key. The mutation accepts the list of upload_ids
// to delete.
export const useUploadDelete = (project_id: string | null | undefined) => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<DeleteResponse, Error, string[]>(
    async (upload_ids) =>
      http<DeleteResponse>("/uploads/delete", { upload_ids }),
    {
      onSuccess: () => {
        if (project_id) {
          // Re-fetch the project's flat file list so the deleted rows disappear.
          qc.invalidateQueries({ queryKey: queryKeys.uploadsList(project_id) });
          // AND the synced-files TREE (Drive/Gmail panels), or a deleted
          // Drive-sourced file lingers there.
          qc.invalidateQueries({ queryKey: queryKeys.uploadsTree(project_id) });
        }
        // Deleting files can change the dashboard's failed-file / new-files chips.
        invalidateOperationsDashboard(qc);
      },
    },
  );
};

// Hook: refresh one or more Drive- OR Gmail-sourced uploads.
//
// Drive rows: acquire a Drive access token, re-fetch the bytes by the
// stored drive_file_id, PUT them to the refresh presigned URL, and
// call /uploads/refresh_complete to bump the recorded etag/size.
//
// Gmail rows: acquire a Gmail access token, re-pull the thread
// (fetchThreadStructured) + re-stage every attachment <= the size cap
// via the SAME ``pullAttachments`` the import path uses, reassemble the
// thread JSON manifest (assembleThreadManifest), PUT it to the refresh
// presigned URL, then /uploads/refresh_complete - which re-enqueues
// extraction, and with it the backend promotion pass that turns the
// re-staged attachments back into first-class uploads.
//
// Sharing pullAttachments is load-bearing, not just tidiness: this path
// used to carry its own copy of the staging loop that omitted message_id
// and content_id, so refreshing a thread silently destroyed every
// attachment's attribution to its message and every inline image's
// [cid:] anchor. Re-pulling attachments keeps the manifest consistent
// with the freshly-pulled thread (CLAUDE.md principle 7: new input
// invalidates ALL derived state).
//
// Partial-success shape: returns ``{refreshed, errors}`` instead of
// throwing on the first failure, so a bulk refresh of N files
// surfaces each row's outcome to the operator (some rows may be
// non-refreshable, some Drive files may have been removed from Drive,
// etc.). The caller iterates over the result.
//
// Concurrency cap of 4 keeps the download path from monopolising the
// operator's per-host TCP connection pool while still saving wall
// time over a serial loop. The same limit drives the picker's
// confirm-batch in DrivePickerPanel. The Drive token is acquired only
// when there's a Drive row to refresh, the Gmail token only when
// there's a Gmail row - so a pure-Drive or pure-Gmail batch prompts
// at most one consent popup.
export const useUploadRefresh = (
  project_id: string | null | undefined,
) => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  const { user } = useKindeAuth();
  return useMutation<RefreshResult, Error, UploadRecord[]>(
    async (records) => {
      const driveRecords = records.filter(
        (r): r is UploadRecord & { drive_file_id: string } =>
          r.source === "drive" && typeof r.drive_file_id === "string",
      );
      const gmailRecords = records.filter(
        (r): r is UploadRecord & { gmail_thread_id: string } =>
          r.source === "gmail" && typeof r.gmail_thread_id === "string",
      );
      if (driveRecords.length === 0 && gmailRecords.length === 0) {
        return { refreshed: [], errors: [] };
      }

      const refreshed: string[] = [];
      const errors: RefreshResult["errors"] = [];

      // --- Drive path ---
      if (driveRecords.length > 0) {
        await ensureGsiLoaded();
        const driveToken = await requestGoogleAccessToken([GOOGLE_DRIVE_SCOPE]);

        const refreshDriveOne = async (
          record: (typeof driveRecords)[number],
        ) => {
          const init = await http<RefreshInitiateResponse>(
            "/uploads/refresh_initiate",
            { upload_id: record.upload_id },
          );
          if (!init.drive_file_id) {
            throw new Error("refresh_initiate returned no drive_file_id");
          }
          const fresh = await refetchDriveFileById(
            init.drive_file_id,
            driveToken,
          );
          const putResp = await fetch(init.presigned_url, {
            method: "PUT",
            body: fresh,
            headers: { "content-type": fresh.type || record.content_type },
          });
          if (!putResp.ok) {
            const detail = await putResp.text();
            throw new Error(
              `S3 PUT failed (${putResp.status}): ${detail.slice(0, 200)}`,
            );
          }
          await http("/uploads/refresh_complete", {
            upload_id: record.upload_id,
          });
        };

        const { errors: workerErrors } = await runWithConcurrency(
          driveRecords,
          async (record) => {
            try {
              await refreshDriveOne(record);
              refreshed.push(record.upload_id);
            } catch (e) {
              errors.push({
                upload_id: record.upload_id,
                message: e instanceof Error ? e.message : String(e),
              });
            }
          },
          { concurrency: 4 },
        );
        // runWithConcurrency catches errors per-item and pushes them
        // onto its own errors array; we've already wrapped each
        // refreshDriveOne in try/catch above, so workerErrors should
        // be empty. Surface anything that slipped through as a
        // belt-and-braces guard.
        for (const we of workerErrors) {
          errors.push({
            upload_id: we.item.upload_id,
            message: we.error.message,
          });
        }
      }

      // --- Gmail path ---
      if (gmailRecords.length > 0) {
        await ensureGsiLoaded();
        const gmailToken = await requestGoogleAccessToken([GOOGLE_GMAIL_SCOPE]);
        const pulled_from = await getThreadProfileEmail(gmailToken);

        const refreshGmailOne = async (
          record: (typeof gmailRecords)[number],
        ) => {
          // Defence-in-depth: the Files tab disables the Refresh
          // button unless the signed-in user imported the thread, but
          // re-check here so a programmatic caller can't bypass it.
          if (record.pulled_by && user?.id && record.pulled_by !== user.id) {
            throw new Error(
              `refreshable only by ${record.pulled_by_name ?? "the importer"}`,
            );
          }
          const init = await http<RefreshInitiateResponse>(
            "/uploads/refresh_initiate",
            { upload_id: record.upload_id },
          );
          const threadId = init.gmail_thread_id ?? record.gmail_thread_id;
          if (!threadId) {
            throw new Error("refresh_initiate returned no gmail_thread_id");
          }
          const structured = await fetchThreadStructured(threadId, gmailToken);

          const manifestAttachments = await pullAttachments(
            structured,
            record.upload_id,
            record.project_id,
            gmailToken,
            http,
          );

          const manifest = assembleThreadManifest(
            structured,
            pulled_from,
            manifestAttachments,
          );
          const putResp = await fetch(init.presigned_url, {
            method: "PUT",
            body: JSON.stringify(manifest),
            headers: { "content-type": "application/json" },
          });
          if (!putResp.ok) {
            const detail = await putResp.text();
            throw new Error(
              `S3 PUT failed (${putResp.status}): ${detail.slice(0, 200)}`,
            );
          }
          await http("/uploads/refresh_complete", {
            upload_id: record.upload_id,
          });
        };

        // Sequential: a gmail refresh re-pulls a whole thread +
        // attachments, so the per-record work is already heavy; the
        // 25-result search cap bounds the batch.
        for (const record of gmailRecords) {
          try {
            await refreshGmailOne(record);
            refreshed.push(record.upload_id);
          } catch (e) {
            errors.push({
              upload_id: record.upload_id,
              message: e instanceof Error ? e.message : String(e),
            });
          }
        }
      }

      return { refreshed, errors };
    },
    {
      onSuccess: () => {
        if (project_id) {
          qc.invalidateQueries({ queryKey: queryKeys.uploadsList(project_id) });
          // A refresh re-pulls Drive/Gmail attachments, which can add / change
          // rows in the synced-files TREE panel too - invalidate it alongside
          // the flat list (same coupling the W9 useUploadDelete fix restored).
          qc.invalidateQueries({ queryKey: queryKeys.uploadsTree(project_id) });
        }
        // Refreshing Drive files can add / change files the dashboard
        // surfaces (new-files badge, failed-file chip, cross-doc conflicts).
        invalidateOperationsDashboard(qc);
      },
    },
  );
};
